import type { OpenRouterSettings, RecordKind } from './types'

type AgentTaskStatus =
  | 'queued'
  | 'running'
  | 'awaiting_review'
  | 'completed'
  | 'failed'
  | 'cancelled'

interface AgentTaskError {
  code: string
  message: string
  retryable?: boolean
}

interface AgentTaskSnapshot {
  error?: AgentTaskError
  progress?: number
  result?: unknown
  status: AgentTaskStatus
  step: string
  taskId: string
  taskType: string
  updatedAt: string
}

interface CommandAccepted {
  status: AgentTaskStatus | 'queued' | 'running' | 'completed' | 'failed'
  taskId: string
}

interface CommandEnvelope<TPayload> {
  correlationId: string
  id: string
  idempotencyKey: string
  payload: TPayload
  tenantId: string
  timestamp: string
  type: string
  userId: string
  version: number
}

interface ChatCommandPayload {
  context: {
    includeMedications: boolean
    includeTimeline: boolean
    workspace?: AgentWorkspaceContext
  }
  messageId: string
  mode: 'deferred' | 'interactive'
  responseFormat: 'plain_text' | 'structured'
  text: string
}

export interface AgentWorkspaceRecordSummary {
  kind: RecordKind
  source?: string
  summary: string
  title: string
  updatedAt?: string
}

export interface AgentWorkspaceContext {
  connectedProviders: number
  parsedRecordContext: string
  parsedRecords: number
  parsedRecordsSample: AgentWorkspaceRecordSummary[]
  reportsCount: number
}

interface SettingsUpdatePayload {
  apiKey: string
  model: string
}

export interface AgentUploadParseResult {
  detectedKind: RecordKind
  pageCount: number
  parseSummary: string
  source: string
  textPreview: string
  title: string
  truncated: boolean
}

interface UploadViaAgentInput {
  file: File
  kind: RecordKind
  source: string
  tenantId: string
  title: string
  userId: string
}

interface ChatViaAgentInput {
  context?: AgentWorkspaceContext
  tenantId: string
  text: string
  userId: string
}

interface SettingsViaAgentInput {
  settings: OpenRouterSettings
  tenantId: string
  userId: string
}

const POLL_INTERVAL_MS = 700
const POLL_TIMEOUT_MS = 60_000
const AGENT_API_BASE_URL =
  process.env.REACT_APP_AGENT_API_BASE_URL?.trim().replace(/\/+$/, '') ?? ''
const VALID_RECORD_KINDS: RecordKind[] = ['Lab', 'Imaging', 'Visit Note', 'Medication']

function createUuid(): string {
  if (typeof globalThis.crypto?.randomUUID !== 'function') {
    throw new Error('[CRYPTO_UUID_UNAVAILABLE] Browser crypto.randomUUID() is required.')
  }

  return globalThis.crypto.randomUUID()
}

function createIdempotencyKey(prefix: string): string {
  return `${prefix}-${createUuid()}`
}

function normalizeApiKey(rawApiKey: string): string {
  return rawApiKey.trim().replace(/^Bearer\s+/i, '')
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isRecordKind(value: string): value is RecordKind {
  return VALID_RECORD_KINDS.includes(value as RecordKind)
}

function resolveErrorMessage(response: Response, payload: unknown): string {
  if (isObject(payload) && isObject(payload.error)) {
    const code =
      typeof payload.error.code === 'string' && payload.error.code.trim().length > 0
        ? payload.error.code.trim()
        : 'AGENT_API_HTTP_ERROR'
    const message =
      typeof payload.error.message === 'string' && payload.error.message.trim().length > 0
        ? payload.error.message.trim()
        : `Agent API request failed with status ${response.status}.`

    return `[${code}] ${message}`
  }

  return `[AGENT_API_HTTP_ERROR] Agent API request failed with status ${response.status}.`
}

async function parseResponsePayload(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown
  } catch {
    if (!response.ok) {
      throw new Error(`[AGENT_API_HTTP_ERROR] Agent API request failed with status ${response.status}.`)
    }

    throw new Error('[AGENT_API_INVALID_JSON] Agent API returned invalid JSON payload.')
  }
}

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  if (!isAgentApiEnabled()) {
    throw new Error('[AGENT_API_NOT_CONFIGURED] Set REACT_APP_AGENT_API_BASE_URL.')
  }

  const response = await fetch(`${AGENT_API_BASE_URL}${path}`, init)
  const payload = await parseResponsePayload(response)

  if (!response.ok) {
    throw new Error(resolveErrorMessage(response, payload))
  }

  return payload as T
}

function normalizeRecordKind(value: unknown): RecordKind | null {
  if (typeof value !== 'string') {
    return null
  }

  const normalized = value.trim().toLowerCase()
  if (normalized === 'lab') return 'Lab'
  if (normalized === 'imaging') return 'Imaging'
  if (normalized === 'medication') return 'Medication'
  if (normalized === 'visit note' || normalized === 'visit_note' || normalized === 'visitnote') {
    return 'Visit Note'
  }

  return null
}

function parseAcceptedResponse(payload: unknown): CommandAccepted {
  if (!isObject(payload)) {
    throw new Error('[AGENT_ACCEPTED_INVALID] Agent API accepted response payload is invalid.')
  }

  const taskId = typeof payload.taskId === 'string' ? payload.taskId : ''
  const status = typeof payload.status === 'string' ? payload.status : ''

  if (!taskId || !status) {
    throw new Error('[AGENT_ACCEPTED_INVALID] Agent API accepted response is missing task metadata.')
  }

  return {
    taskId,
    status: status as CommandAccepted['status'],
  }
}

function parseTaskSnapshot(payload: unknown): AgentTaskSnapshot {
  if (!isObject(payload)) {
    throw new Error('[AGENT_TASK_INVALID] Agent API task payload is invalid.')
  }

  const taskId = typeof payload.taskId === 'string' ? payload.taskId : ''
  const taskType = typeof payload.taskType === 'string' ? payload.taskType : ''
  const status = typeof payload.status === 'string' ? payload.status : ''
  const step = typeof payload.step === 'string' ? payload.step : ''
  const updatedAt = typeof payload.updatedAt === 'string' ? payload.updatedAt : ''

  if (!taskId || !taskType || !status || !step || !updatedAt) {
    throw new Error('[AGENT_TASK_INVALID] Agent API task payload is missing required fields.')
  }

  const parsed: AgentTaskSnapshot = {
    taskId,
    taskType,
    status: status as AgentTaskStatus,
    step,
    updatedAt,
    result: payload.result,
  }

  if (typeof payload.progress === 'number') {
    parsed.progress = payload.progress
  }

  if (isObject(payload.error)) {
    const code = typeof payload.error.code === 'string' ? payload.error.code.trim() : ''
    const message = typeof payload.error.message === 'string' ? payload.error.message.trim() : ''
    if (!code || !message) {
      throw new Error('[AGENT_TASK_INVALID_ERROR] Agent task error payload is missing code or message.')
    }

    parsed.error = {
      code,
      message,
      retryable: typeof payload.error.retryable === 'boolean' ? payload.error.retryable : undefined,
    }
  }

  if ((parsed.status === 'failed' || parsed.status === 'cancelled') && !parsed.error) {
    throw new Error('[AGENT_TASK_INVALID_ERROR] Failed task payload must include a structured error.')
  }

  return parsed
}

function formatTaskFailure(
  task: AgentTaskSnapshot,
  defaultCode: string,
  defaultMessage: string
): string {
  const code = task.error?.code?.trim() || defaultCode
  const message = task.error?.message?.trim() || defaultMessage
  return `[${code}] ${message}`
}

async function submitCommand<TPayload>(
  type: string,
  payload: TPayload,
  tenantId: string,
  userId: string,
  idempotencyKey?: string
): Promise<CommandAccepted> {
  const envelope: CommandEnvelope<TPayload> = {
    id: createUuid(),
    type,
    version: 1,
    tenantId,
    userId,
    correlationId: createUuid(),
    idempotencyKey: idempotencyKey ?? createIdempotencyKey('idem'),
    timestamp: new Date().toISOString(),
    payload,
  }

  const payloadResponse = await fetchJson<unknown>('/v1/commands', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(envelope),
  })

  return parseAcceptedResponse(payloadResponse)
}

async function getTask(taskId: string): Promise<AgentTaskSnapshot> {
  const payload = await fetchJson<unknown>(`/v1/tasks/${taskId}`, {
    method: 'GET',
  })

  return parseTaskSnapshot(payload)
}

async function pollTask(taskId: string): Promise<AgentTaskSnapshot> {
  const startedAt = Date.now()

  while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
    const task = await getTask(taskId)
    if (task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled') {
      return task
    }

    await new Promise((resolve) => {
      window.setTimeout(resolve, POLL_INTERVAL_MS)
    })
  }

  throw new Error(
    `[AGENT_TASK_TIMEOUT] Agent task timed out after ${Math.round(POLL_TIMEOUT_MS / 1000)} seconds.`
  )
}

function parseChatResult(task: AgentTaskSnapshot): string {
  if (!isObject(task.result)) {
    throw new Error('[AGENT_CHAT_RESULT_INVALID] Agent chat result is missing result payload.')
  }

  const content = typeof task.result.assistantMessage === 'string' ? task.result.assistantMessage.trim() : ''
  if (!content) {
    throw new Error('[AGENT_CHAT_RESULT_INVALID] Agent chat result did not include assistant content.')
  }

  return content
}

function parseUploadResult(task: AgentTaskSnapshot): AgentUploadParseResult {
  if (!isObject(task.result) || !isObject(task.result.record)) {
    throw new Error('[AGENT_UPLOAD_RESULT_INVALID] Agent upload task did not return parsed record output.')
  }

  const resultRecord = task.result.record
  const kind = normalizeRecordKind(resultRecord.kind)

  if (!kind) {
    throw new Error('[AGENT_UPLOAD_RESULT_INVALID] Agent upload result returned an invalid record kind.')
  }

  const parseSummary = typeof resultRecord.parseSummary === 'string' ? resultRecord.parseSummary : ''
  if (!parseSummary.trim()) {
    throw new Error('[AGENT_UPLOAD_RESULT_INVALID] Agent upload result is missing parse summary.')
  }

  const title = typeof resultRecord.title === 'string' ? resultRecord.title.trim() : ''
  if (!title) {
    throw new Error('[AGENT_UPLOAD_RESULT_INVALID] Agent upload result is missing resolved title.')
  }

  const source = typeof resultRecord.source === 'string' ? resultRecord.source.trim() : ''
  if (!source) {
    throw new Error('[AGENT_UPLOAD_RESULT_INVALID] Agent upload result is missing resolved source.')
  }

  const textPreview = typeof resultRecord.textPreview === 'string' ? resultRecord.textPreview : null
  if (textPreview === null) {
    throw new Error('[AGENT_UPLOAD_RESULT_INVALID] Agent upload result is missing text preview.')
  }

  const pageCount =
    typeof resultRecord.pageCount === 'number' && Number.isFinite(resultRecord.pageCount)
      ? resultRecord.pageCount
      : 0
  if (pageCount < 1) {
    throw new Error('[AGENT_UPLOAD_RESULT_INVALID] Agent upload result returned an invalid page count.')
  }

  if (typeof resultRecord.truncated !== 'boolean') {
    throw new Error('[AGENT_UPLOAD_RESULT_INVALID] Agent upload result is missing truncated flag.')
  }

  return {
    detectedKind: kind,
    pageCount,
    parseSummary,
    source,
    textPreview,
    title,
    truncated: resultRecord.truncated,
  }
}

export function isAgentApiEnabled(): boolean {
  return AGENT_API_BASE_URL.length > 0
}

export function getAgentApiBaseUrl(): string | null {
  return isAgentApiEnabled() ? AGENT_API_BASE_URL : null
}

export async function syncSettingsWithAgentApi({
  settings,
  tenantId,
  userId,
}: SettingsViaAgentInput): Promise<void> {
  if (!isAgentApiEnabled()) {
    return
  }

  const normalizedModel = settings.model.trim()
  if (!normalizedModel) {
    throw new Error('[SETTINGS_MODEL_REQUIRED] OpenRouter model is required.')
  }

  const payload: SettingsUpdatePayload = {
    apiKey: normalizeApiKey(settings.apiKey),
    model: normalizedModel,
  }

  // Use a fresh idempotency key per sync attempt so agent restarts can re-apply settings.
  const accepted = await submitCommand('settings.update', payload, tenantId, userId)

  const task = await pollTask(accepted.taskId)
  if (task.status === 'failed' || task.status === 'cancelled') {
    throw new Error(formatTaskFailure(task, 'SETTINGS_SYNC_FAILED', 'Failed to sync settings with agent API.'))
  }
}

export async function requestChatAnswerViaAgentApi({
  context,
  tenantId,
  text,
  userId,
}: ChatViaAgentInput): Promise<string> {
  const payload: ChatCommandPayload = {
    messageId: createUuid(),
    mode: 'interactive',
    responseFormat: 'plain_text',
    text,
    context: {
      includeMedications: true,
      includeTimeline: true,
      workspace: context,
    },
  }

  const accepted = await submitCommand('chat.answer', payload, tenantId, userId)
  const task = await pollTask(accepted.taskId)

  if (task.status === 'failed' || task.status === 'cancelled') {
    throw new Error(formatTaskFailure(task, 'CHAT_TASK_FAILED', 'Agent chat task failed.'))
  }

  return parseChatResult(task)
}

export async function uploadAndParseRecordViaAgentApi({
  file,
  kind,
  source,
  tenantId,
  title,
  userId,
}: UploadViaAgentInput): Promise<AgentUploadParseResult> {
  if (!isAgentApiEnabled()) {
    throw new Error('[AGENT_API_NOT_CONFIGURED] Set REACT_APP_AGENT_API_BASE_URL.')
  }

  const normalizedTenantId = tenantId.trim()
  const normalizedUserId = userId.trim()
  const normalizedTitle = title.trim()
  const normalizedSource = source.trim()
  const normalizedKind = isRecordKind(kind) ? kind : null

  if (
    !normalizedTenantId ||
    !normalizedUserId ||
    !normalizedTitle ||
    !normalizedSource ||
    !normalizedKind
  ) {
    throw new Error(
      '[AGENT_UPLOAD_REQUEST_INVALID] tenantId, userId, title, source, and a valid kind are required.'
    )
  }

  const formData = new FormData()
  formData.append('file', file)
  formData.append('tenantId', normalizedTenantId)
  formData.append('userId', normalizedUserId)
  formData.append('title', normalizedTitle)
  formData.append('source', normalizedSource)
  formData.append('kind', normalizedKind)

  const response = await fetch(`${AGENT_API_BASE_URL}/v1/uploads/complete`, {
    method: 'POST',
    body: formData,
  })

  const payload = await parseResponsePayload(response)
  if (!response.ok) {
    throw new Error(resolveErrorMessage(response, payload))
  }

  const accepted = parseAcceptedResponse(payload)
  const task = await pollTask(accepted.taskId)
  if (task.status === 'failed' || task.status === 'cancelled') {
    throw new Error(formatTaskFailure(task, 'UPLOAD_TASK_FAILED', 'Agent upload task failed.'))
  }

  return parseUploadResult(task)
}
